import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Printer, RotateCcw } from "lucide-react";
import MenuLateral from "../../Componentes/MenuLateral";
import MenuSuperior from "../../Componentes/MenuSuperior";
import api from "../../Services/api";
import { useOperador } from "../../Hooks/useOperador";
import { operadorEhAdminOuGerente } from "../../Utils/cargos";
import "./styles.css";

const filtrosIniciais = {
  IDOperador: "",
  IDCliente: "",
  dataInicio: "",
  dataFim: "",
};

const formatarData = (valor) => {
  if (!valor) return "";
  const data = new Date(String(valor).replace(/\//g, "-"));
  if (Number.isNaN(data.getTime())) return valor;
  return data.toLocaleString("pt-BR");
};

const formatarOperador = (item) => {
  const id = item.IDOperador ? String(item.IDOperador).padStart(6, "0") : "";
  return [id, item.operadorNome].filter(Boolean).join(" - ");
};

const SisAuditoriaConsulta = () => {
  const navigate = useNavigate();
  const { IDOperador, nome, operador } = useOperador();
  const [filtros, setFiltros] = useState(filtrosIniciais);
  const [operadores, setOperadores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);

  const podeConsultar = operadorEhAdminOuGerente(operador);

  useEffect(() => {
    if (!podeConsultar) {
      navigate("/Menu");
      return;
    }

    const carregarCombos = async () => {
      try {
        const [operadoresRes, clientesRes] = await Promise.all([
          api.get("/operadores"),
          api.get("/clientes"),
        ]);

        setOperadores(Array.isArray(operadoresRes.data) ? operadoresRes.data : []);
        setClientes(Array.isArray(clientesRes.data) ? clientesRes.data : []);
      } catch (error) {
        console.error("Erro ao carregar filtros de auditoria:", error);
      }
    };

    carregarCombos();
  }, [podeConsultar, navigate]);

  const params = useMemo(() => {
    return Object.entries(filtros).reduce((acc, [chave, valor]) => {
      if (valor) acc[chave] = valor;
      return acc;
    }, {});
  }, [filtros]);

  const consultarAuditoria = async () => {
    setLoading(true);
    try {
      const response = await api.get("/sis_auditoria", { params });
      setRegistros(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Erro ao consultar auditoria:", error.response?.data || error);
      setRegistros([]);
    } finally {
      setLoading(false);
    }
  };

  const limparFiltros = () => {
    setFiltros(filtrosIniciais);
    setRegistros([]);
  };

  const alterarFiltro = (campo, valor) => {
    setFiltros((atual) => ({ ...atual, [campo]: valor }));
  };

  return (
    <div className="menu-container-menu auditoria-consulta-page">
      <MenuLateral />
      <div id="painel_principal_menu" align="center">
        <MenuSuperior userId={String(IDOperador).padStart(6, "0")} userName={nome} />

        <main className="auditoria-consulta">
          <header className="auditoria-consulta__header">
            <h3>Consulta de Auditoria</h3>
            <button type="button" className="btn btn-outline-secondary" onClick={() => window.print()}>
              <Printer size={18} />
              Imprimir
            </button>
          </header>

          <section className="auditoria-consulta__filters">
            <label>
              Operador
              <select value={filtros.IDOperador} onChange={(e) => alterarFiltro("IDOperador", e.target.value)}>
                <option value="">Todos</option>
                {operadores.map((operador) => (
                  <option key={operador.IDOperador} value={operador.IDOperador}>
                    {String(operador.IDOperador).padStart(6, "0")} - {operador.nome}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Cliente
              <select value={filtros.IDCliente} onChange={(e) => alterarFiltro("IDCliente", e.target.value)}>
                <option value="">Todos</option>
                {clientes.map((cliente) => (
                  <option key={cliente.IDCliente} value={cliente.IDCliente}>
                    {cliente.nome}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Data inicio
              <input
                type="date"
                value={filtros.dataInicio}
                onChange={(e) => alterarFiltro("dataInicio", e.target.value)}
              />
            </label>

            <label>
              Data fim
              <input
                type="date"
                value={filtros.dataFim}
                onChange={(e) => alterarFiltro("dataFim", e.target.value)}
              />
            </label>

            <div className="auditoria-consulta__actions">
              <button type="button" className="btn btn-primary" onClick={consultarAuditoria} disabled={loading}>
                <Search size={18} />
                {loading ? "Consultando..." : "Consultar"}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={limparFiltros}>
                <RotateCcw size={18} />
                Limpar
              </button>
            </div>
          </section>

          <section className="auditoria-consulta__result">
            <div className="auditoria-consulta__summary">
              <strong>{registros.length}</strong> registro(s) encontrado(s)
            </div>

            <div className="auditoria-consulta__table-wrap">
              <table className="auditoria-consulta__table">
                <colgroup>
                  <col className="auditoria-consulta__col-data" />
                  <col className="auditoria-consulta__col-operador" />
                  <col className="auditoria-consulta__col-cliente" />
                  <col className="auditoria-consulta__col-operacao" />
                  <col className="auditoria-consulta__col-arquivo" />
                  <col className="auditoria-consulta__col-diretorio" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Operador</th>
                    <th>Cliente</th>
                    <th>Operacao</th>
                    <th>Arquivo</th>
                    <th>Diretorio</th>
                  </tr>
                </thead>
                <tbody>
                  {registros.map((item) => (
                    <tr key={item.IDsisauditoria}>
                      <td>{formatarData(item.Data)}</td>
                      <td>{formatarOperador(item)}</td>
                      <td>{item.clienteNome || "-"}</td>
                      <td>{item.texto}</td>
                      <td>{item.nomearquivo || "-"}</td>
                      <td>{item.nomediretorio || "-"}</td>
                    </tr>
                  ))}

                  {!loading && registros.length === 0 && (
                    <tr>
                      <td colSpan="6" className="auditoria-consulta__empty">
                        Nenhum registro para exibir.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default SisAuditoriaConsulta;
